import { apiFetch } from '@crowdex/host-kit/renderer/http';
import type {
  SourceControlRepository as BaseSourceControlRepository,
  SourceControlRepositoriesData,
} from '@crowdex/host-kit/renderer/repository-types';
import type { PullRequestSummary } from '@crowdex/workspace-protocol';
import { cloudApiPath } from '@/lib/cloud-link';
import { isLocalMachine } from '@/lib/local-runtime';

const SOURCE_CONTROL_ROUTE = '/api/source-control';

export type SourceControlRepository = BaseSourceControlRepository & {
  /** Whether Crowdex Cloud's source control holds it rather than this server's. */
  cloud?: boolean;
};

export type SourceControlConnectorState = Omit<SourceControlRepositoriesData, 'repositories'> & {
  repositories: readonly SourceControlRepository[];
};

/** A branch of the repository a cloud workspace starts from (its default branch otherwise). */
export type SourceControlWorkspaceSource = { type: 'remote'; ref: string };

export async function fetchSourceControlRepositories(): Promise<SourceControlConnectorState> {
  return apiFetch<SourceControlConnectorState>(`${SOURCE_CONTROL_ROUTE}/repositories`, {
    credentials: 'include',
    cache: 'no-store',
  });
}

/** A repository by its git remote URL, or by `owner/name` on any of the user's forges. */
export async function resolveSourceControlRepository(
  remote: string,
  cloud = false,
): Promise<SourceControlRepository> {
  const repository = await apiFetch<SourceControlRepository>(
    cloudApiPath(cloud, `${SOURCE_CONTROL_ROUTE}/repositories/resolve`),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ remote }),
    },
  );
  return { ...repository, cloud };
}

export async function fetchSourceControlRepositoryBranches(
  repository: SourceControlRepository,
): Promise<readonly string[]> {
  const query = new URLSearchParams({ host: repository.host, repository: repository.fullName });
  const result = await apiFetch<{ branches: readonly string[] }>(
    cloudApiPath(repository.cloud, `${SOURCE_CONTROL_ROUTE}/repositories/branches?${query}`),
    { credentials: 'include', cache: 'no-store' },
  );
  return result.branches;
}

export async function checkoutSourceControlRepository(
  repository: SourceControlRepository,
  taskId: string,
  source?: SourceControlWorkspaceSource,
  pullRequest?: PullRequestSummary | null,
): Promise<string> {
  const result = await apiFetch<{ workspaceRoot: string }>(
    cloudApiPath(repository.cloud, `${SOURCE_CONTROL_ROUTE}/workspaces`),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        taskId,
        repository: { host: repository.host, fullName: repository.fullName },
        ...(source === undefined ? {} : { remoteRef: source.ref }),
        ...(pullRequest === undefined ? {} : { pullRequest }),
      }),
    },
  );
  return result.workspaceRoot;
}

export function sourceControlRepositoryWorkspaceRoot(repository: SourceControlRepository): string {
  return repository.workspaceRoot;
}

export async function configureSourceControlRepositoryForWorkspace(
  workspaceRoot: string,
  taskId: string,
): Promise<string> {
  const state = await fetchSourceControlRepositories().catch(() => null);
  const repository = state?.repositories.find(
    (candidate) => sourceControlRepositoryWorkspaceRoot(candidate) === workspaceRoot,
  );
  if (repository !== undefined) return await checkoutSourceControlRepository(repository, taskId);
  // Not among the connected forge's repositories (or no forge connected): the cloud opens its
  // own project, from the repository it keeps of it.
  const result = await apiFetch<{ workspaceRoot: string }>(
    // A machine reaches the cloud project through its Crowdex Cloud link.
    cloudApiPath(isLocalMachine(), `${SOURCE_CONTROL_ROUTE}/workspaces`),
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ taskId, workspaceRoot }),
    },
  );
  return result.workspaceRoot;
}
